<template>
    <div class="image-upload">
        <div v-if="label" class="font-weight-bold mb-2" v-html="label"></div>

        <div class="d-flex flex-wrap">
            <div class="image-upload-preview-wrapper mr-5 mb-3">
                <div class="image-upload-preview" :class="`image-upload-preview-${type}`">
                    <img v-if="displayed_src !== ''"
                         :src="displayed_src"
                         class="image-upload-image"
                         alt="">
                    <div v-else class="image-upload-empty grey--text">
                        <v-icon>mdi-image-off-outline</v-icon>
                        <div v-html="$th('image-upload.no-image')"></div>
                    </div>

                    <div v-if="busy" class="image-upload-busy">
                        <v-progress-circular indeterminate
                                             size="32"
                                             :color="$variables.v.theme.secondary"></v-progress-circular>
                    </div>
                </div>
                <div class="caption grey--text mt-1" v-html="status_text"></div>
            </div>

            <div class="image-upload-controls mb-3">
                <div class="d-flex flex-wrap">
                    <v-btn small
                           outlined
                           :disabled="busy"
                           class="mr-2 mb-2"
                           @click="$refs.file_input.click()">
                        <v-icon left small>mdi-upload</v-icon>
                        <span v-html="$th('image-upload.upload')"></span>
                    </v-btn>

                    <v-btn v-if="can_remove"
                           small
                           outlined
                           color="error"
                           :disabled="busy"
                           class="mr-2 mb-2"
                           @click="remove_image">
                        <v-icon left small>mdi-trash-can-outline</v-icon>
                        <span v-html="$th('image-upload.remove')"></span>
                    </v-btn>

                    <v-btn v-if="value !== ''"
                           small
                           text
                           :disabled="busy"
                           class="mb-2"
                           @click="reset_image">
                        <v-icon left small>mdi-undo</v-icon>
                        <span v-html="$th('image-upload.undo')"></span>
                    </v-btn>
                </div>

                <input ref="file_input"
                       type="file"
                       accept="image/jpeg,image/png,image/gif,image/webp"
                       class="d-none"
                       @change="on_file_selected">

                <div class="d-flex align-start">
                    <v-text-field v-model="url_input"
                                  :label="$t('image-upload.url-label')"
                                  :color="$variables.v.theme.secondary"
                                  :disabled="busy"
                                  outlined
                                  dense
                                  hide-details
                                  @keydown.enter.prevent="load_from_url"></v-text-field>
                    <v-btn small
                           depressed
                           class="ml-2 mt-1"
                           :color="$variables.v.theme.primary"
                           :disabled="busy || url_input.trim() === ''"
                           @click="load_from_url">
                        <span v-html="$th('image-upload.load')"></span>
                    </v-btn>
                </div>

                <div class="caption grey--text mt-1" v-html="$th('image-upload.hint')"></div>
            </div>
        </div>
    </div>
</template>

<script>
    // Image picker that uploads a file, or downloads an image from a url on the server.
    // The processed image is stored as a temporary upload and the component only reports a token through v-model:
    //   ""         nothing changed, the current image is kept
    //   "remove"   the current image is deleted when the form is saved
    //   <token>    the uploaded image replaces the current one when the form is saved
    export default {
        props   : {
            value      : {type: String, default: ""},
            type       : {type: String, required: true}, // post, avatar or background
            current_url: {type: String, default: ""},
            label      : {type: String, default: ""},
        },
        data() {
            return {
                busy            : false,
                preview         : "", // Data url of the processed image that has been uploaded but not saved yet
                url_input       : "",
                max_upload_bytes: 5 * 1024 * 1024,
            };
        },
        computed: {
            // The current image is only shown when it is stored on this server
            current_src() {
                return this.func.local_image_url(this.current_url);
            },
            displayed_src() {
                if (this.value === "remove")
                    return "";
                if (this.value !== "" && this.preview !== "")
                    return this.preview;

                return this.current_src;
            },
            can_remove() {
                // Removing makes sense while there is a current image or a new one that has not been saved yet
                return this.value === "" ? this.current_src !== "" : this.value !== "remove";
            },
            status_text() {
                if (this.value === "remove")
                    return this.$t("image-upload.will-be-removed");
                if (this.value !== "")
                    return this.$t("image-upload.new-image");

                return this.current_src !== "" ? this.$t("image-upload.current-image") : "";
            },
        },
        watch   : {
            // A reset from the parent (e.g. after a post has been created) also drops the local preview
            value() {
                if (this.value === "" || this.value === "remove")
                    this.preview = "";
            },
        },
        methods : {
            build_form_data() {
                const form_data = new FormData();
                form_data.append("userid", this.$user.user.id);
                form_data.append("token", this.$user.user.token);
                form_data.append("type", this.type);
                return form_data;
            },
            async send_image(form_data) {
                this.busy = true;
                let response;

                try {
                    const result = await this.func.axios.post("/php/image_upload.php", form_data);
                    response     = result.data;
                } catch (error) {
                    response = "php.image-upload-failed";
                }

                if (this.func.isObject(response) && response.token) {
                    this.preview   = response.preview;
                    this.url_input = "";
                    this.$emit("input", response.token);
                } else {
                    const is_error_code = typeof response === "string" && response.startsWith("php.");
                    this.eventbus.$emit("SB", is_error_code ? response : "php.image-upload-failed", "error");
                }

                this.busy = false;
            },
            async on_file_selected(event) {
                const file = event.target.files[0];
                // Resetting the input allows choosing the same file again
                event.target.value = "";

                if (!file)
                    return;

                // The server checks the size as well, this only spares the upload of files that are clearly too large
                if (file.size > this.max_upload_bytes) {
                    this.eventbus.$emit("SB", "php.image-too-large", "error");
                    return;
                }

                const form_data = this.build_form_data();
                form_data.append("file", file);
                await this.send_image(form_data);
            },
            async load_from_url() {
                const url = this.url_input.trim();
                if (url === "")
                    return;

                const form_data = this.build_form_data();
                form_data.append("url", this.func.add_http(url));
                await this.send_image(form_data);
            },
            remove_image() {
                // Without a stored image there is nothing to delete, discarding the new upload is enough
                this.preview = "";
                this.$emit("input", this.current_src !== "" ? "remove" : "");
            },
            reset_image() {
                this.preview = "";
                this.$emit("input", "");
            },
        },
    }
</script>

<style scoped>
    .image-upload {
        max-width : 100%;
    }

    .image-upload-controls {
        flex      : 1 1 260px;
        min-width : 240px;
        max-width : 420px;
    }

    .image-upload-preview {
        position         : relative;
        display          : flex;
        align-items      : center;
        justify-content  : center;
        overflow         : hidden;
        border-radius    : 8px;
        background-color : rgba(30, 30, 30, .5);
    }

    .image-upload-preview-post {
        width  : 200px;
        height : 130px;
    }

    .image-upload-preview-avatar {
        width  : 150px;
        height : 150px;
    }

    .image-upload-preview-background {
        width  : 200px;
        height : 120px;
    }

    .image-upload-image {
        width      : 100%;
        height     : 100%;
        object-fit : contain;
    }

    .image-upload-empty {
        text-align : center;
        font-size  : 12px;
    }

    .image-upload-busy {
        position         : absolute;
        top              : 0;
        right            : 0;
        bottom           : 0;
        left             : 0;
        display          : flex;
        align-items      : center;
        justify-content  : center;
        background-color : rgba(0, 0, 0, .5);
    }
</style>
